/**
 * Parent-filter-changes-invalidate-children, handled generically.
 *
 * The relationship this file acts on is not a fixed "Country is the parent of
 * Course Type" table — it is whatever the search response's own facets say.
 * Every dependent field's facet in a response is already measured with THAT
 * FIELD'S OWN filter clause lifted (see server.js's `countSearches` /
 * universities.js's `filterExcludingUniversity`), so it reports validity under
 * every OTHER currently active filter — which is exactly "is this selection
 * still valid, whatever changed." That means changing the country, the
 * nationality, or any other filter is handled the same way: re-check every
 * dependent selection against the facets the very next response already
 * carries, and drop only the ones that are no longer there.
 *
 * This module is plain data-in, data-out — no React, no fetch — so it can be
 * unit-tested by calling it directly with hand-built fixtures.
 */

/**
 * One dependent field: which filter key holds it, which response facet
 * reports its validity, and how to translate a raw filter value into that
 * facet's own key (most fields use the same string for both; Course Type on
 * Search Programs is the one exception — the filter holds the course NAME
 * while the facet is keyed by the option-1 id, because the id->name mapping
 * is not one-to-one, see server.js `buildFilterClauses`).
 */
function field(key, facet, { toFacetKey = (v) => v, labelOf = (v) => v, fieldLabel } = {}) {
  return { key, facet, toFacetKey, labelOf, fieldLabel };
}

const byNameIn = (list, idKey, nameKey) => {
  const map = new Map((list ?? []).map((o) => [String(o[idKey]), o[nameKey]]));
  return (id) => map.get(String(id)) ?? id;
};

/** Search Programs — dependent fields, in the order they appear on the page. */
export function programFields(options) {
  const nameToId = new Map((options?.courseTypes ?? []).map((t) => [t.name, String(t.id)]));
  return [
    field('degreeLevels', 'course_level_id', {
      toFacetKey: (name) => nameToId.get(name) ?? null,
      fieldLabel: 'Course Type',
    }),
    field('intakeIds', 'intake_ids', {
      labelOf: byNameIn(options?.intakeOptions, 'id', 'name'),
      fieldLabel: 'Course Intake',
    }),
    field('universities', 'university_name', { fieldLabel: 'University Name' }),
    field('durationLabels', 'duration_labels', { fieldLabel: 'Duration' }),
    field('englishTests', 'english_tests', { fieldLabel: 'English Proficiency' }),
  ];
}

/** Universities tab — dependent fields. Every one of these holds at most one
 * value in this app's UI, but the pruning logic below treats every field as a
 * list uniformly, so a genuinely multi-valued field needs no special case. */
export function universityFields(options) {
  return [
    field('universities', 'name', { fieldLabel: 'University Name' }),
    field('intakeIds', 'intake_ids', {
      labelOf: byNameIn(options?.intakeOptions, 'id', 'name'),
      fieldLabel: 'Course Intake',
    }),
    field('courseTypeIds', 'course_level_ids', {
      labelOf: byNameIn(options?.courseTypes, 'id', 'name'),
      fieldLabel: 'Course Type',
    }),
    field('campusIds', 'campus_ids', {
      labelOf: byNameIn(options?.campuses, 'id', 'name'),
      fieldLabel: 'Location',
    }),
    field('educationLevels', 'education_levels', {
      labelOf: byNameIn(options?.highestQualifications, 'id', 'name'),
      fieldLabel: 'Highest Qualification',
    }),
  ];
}

/**
 * Re-check every dependent field's CURRENTLY selected values against the
 * latest facets, and drop only the ones no longer present with a positive
 * count. Everything else — including every value the user picked that is
 * still valid — is left exactly as it was.
 *
 * `filters` is the canonical reducer state (not the wire-format request body,
 * whose field names sometimes differ — e.g. `durationLabels` becomes
 * `durations` on the wire). `facets` is `response.facets` from the search that
 * was just run for these exact filters.
 *
 * Returns `{ patch, removed }`. `patch` is only the fields that actually
 * changed (empty object if nothing did) — safe to skip dispatching when
 * `removed.length === 0`. `removed` is `[{ fieldLabel, valueLabel }]`, for the
 * toast.
 */
export function pruneInvalidSelections(filters, facets, fields) {
  const patch = {};
  const removed = [];
  if (!facets) return { patch, removed };

  for (const f of fields) {
    const current = filters[f.key];
    if (!Array.isArray(current) || current.length === 0) continue;

    // No entry for this facet in the response at all means "not measured this
    // round" (e.g. the field's own filter was empty, so nothing was recounted
    // for it) — never treat silence as "everything is invalid".
    const facetRows = facets[f.facet];
    if (!Array.isArray(facetRows)) continue;

    const valid = new Set(
      facetRows.filter((r) => (r?.count ?? 0) > 0).map((r) => String(r.value)),
    );

    const kept = [];
    for (const value of current) {
      const facetKey = f.toFacetKey(value);
      // A value this module cannot map to a facet key is left alone rather
      // than guessed at — a mapping gap must never masquerade as invalidity.
      if (facetKey == null || valid.has(String(facetKey))) {
        kept.push(value);
      } else {
        removed.push({ fieldLabel: f.fieldLabel, valueLabel: f.labelOf(value) });
      }
    }
    if (kept.length !== current.length) patch[f.key] = kept;
  }

  return { patch, removed };
}

/**
 * The toast copy. One removed value names it; more than one stays generic
 * rather than trying to fit an arbitrary list into a sentence.
 */
export function formatCleanupMessage(removed) {
  if (removed.length === 0) return null;
  if (removed.length === 1) {
    const { fieldLabel, valueLabel } = removed[0];
    return `${fieldLabel} “${valueLabel}” was removed because it isn’t available with your other filters.`;
  }
  return 'Some filters were removed because they are not available for the new selection.';
}
